/** FP1 hardware unit vs FP1-specific gateway (not LPS gateway). */
export type Fp1DeviceType = 'fp1' | 'fp1_gateway';

export type Fp1DeviceStatus =
    | 'in_stock'
    | 'deployed'
    | 'maintenance'
    | 'defective'
    | 'unknown'
    | 'returned'
    | 'decommissioned';

/**
 * Definitive subscription classification emitted by the backend
 * (AdminFp1DeviceController::classifySubscription). The frontend only maps these
 * tokens to a label/color — it does not re-derive the bucket.
 *   na             → in-stock / not deployed, no org/athlete
 *   comp           → free / comp order
 *   invoiced       → active external invoice (manually set)
 *   active_athlete → transferred to an athlete paying their own subscription
 *   active_org     → org whose billed seats cover all its responsible pods
 *   partial_org    → org with fewer billed seats than responsible pods
 *   past_due       → most recent subscription payment is past due
 *   no_sub         → deployed pod with no live subscription (residual)
 */
export type Fp1DeviceSubscriptionStatus =
    | 'na'
    | 'comp'
    | 'invoiced'
    | 'active_athlete'
    | 'active_org'
    | 'partial_org'
    | 'past_due'
    | 'no_sub';

type Fp1ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export function formatFp1SubscriptionStatusLabel(
    raw: string | null | undefined,
    isDeployed?: boolean,
): string {
    const s = raw == null ? '' : String(raw).trim().toLowerCase();
    switch (s) {
        case 'comp':
            return 'Comp';
        case 'invoiced':
            return 'Invoiced';
        case 'active_athlete':
            return 'Active Athlete';
        case 'active_org':
            return 'Active Org';
        case 'partial_org':
            return 'Partial Org';
        case 'past_due':
            return 'Past Due';
        case 'no_sub':
            return 'No Sub';
        case 'na':
            return 'N/A';
        // Legacy raw values (single-device snapshots that carry no class yet).
        case 'active':
            return 'Active';
        default:
            // Empty or any other status (canceled, trialing, incomplete, etc.):
            //   deployed pod with no live subscription → "No Sub"
            //   non-deployed pod → "N/A"
            return isDeployed ? 'No Sub' : 'N/A';
    }
}

/** MUI Chip color for a subscription classification token. */
export function fp1SubscriptionStatusColor(raw: string | null | undefined): Fp1ChipColor {
    const s = raw == null ? '' : String(raw).trim().toLowerCase();
    switch (s) {
        case 'active_athlete':
        case 'active_org':
        case 'active':
            return 'success';
        case 'partial_org':
            return 'warning';
        case 'past_due':
            return 'error';
        case 'comp':
            return 'info';
        case 'invoiced':
            return 'secondary';
        default:
            // na, no_sub, cancelled, empty, etc.
            return 'default';
    }
}

export type Fp1DeviceStatusCounts = Record<Fp1DeviceStatus, number> & {
    total: number;
};

export type Fp1InstallationStatus = 'planning' | 'active' | 'inactive' | 'decommissioned';

/** Structured bench / QA data attached to a custody event (not shown on main device grid). */
export type Fp1CustodyEventMetadata = Record<string, unknown> | null;

export interface Fp1CustodyEvent {
    id: number;
    device_uuid: string;
    event_type: string;
    from_status: string | null;
    to_status: string | null;
    installation_uuid: string | null;
    installation_name?: string | null;
    performed_by_uuid: string | null;
    performed_by_name: string | null;
    notes: string | null;
    metadata?: Fp1CustodyEventMetadata;
    created_at: string;
}

/** Stripe subscription row linked to the hierarchy pod (read-only; distinct from FP1 `subscription_status`). */
export interface Fp1DeviceStripeLinkedSubscription {
    subscription_id: string | null;
    subscription_name: string | null;
    subscription_status: string | null;
}

export type Fp1OrgSubscriptionTier = 'bronze' | 'silver' | 'gold';

export interface Fp1DeviceAssignedOrgRef {
    uuid: string;
    name: string | null;
    tier?: Fp1OrgSubscriptionTier | null;
    stripe_customer_id?: string | null;
}

export interface Fp1DeviceAssignedAthleteRef {
    uuid: string;
    name: string | null;
    email: string | null;
}

export interface Fp1DevicePendingTransferRef {
    uuid: string;
    name: string | null;
}

export interface Fp1Device {
    uuid: string;
    serial_number: string;
    device_type: Fp1DeviceType;
    /** Legacy PCB label; prefer `hardware_version` for new shipments. */
    model: string | null;
    /** PCB / hardware revision set at receipt (immutable after shipment create). */
    hardware_version?: string | null;
    /** Firmware on the pod; may be updated after receipt. */
    firmware_version?: string | null;
    status: Fp1DeviceStatus;
    /** Stripe (or comp order); null means N/A — typically warehouse / not linked yet. */
    subscription_status?: Fp1DeviceSubscriptionStatus | string | null;
    /**
     * Set when an admin explicitly removed this pod's subscription status. Such a pod is
     * deliberately on no subscription: it reads "No Sub" and no longer inherits its org's
     * Stripe status. Cleared automatically once a real subscription attaches, when the
     * athlete is detached, or when the pod returns to stock.
     */
    subscription_cleared_at?: string | null;
    /**
     * Set when the athlete attachment was a TRANSFER rather than an org assignment — true
     * even when the athlete had no active subscription to carry, which is what keeps such a
     * pod labelled "Transferred" instead of silently becoming "Assigned".
     */
    athlete_transferred_at?: string | null;
    installation_uuid: string | null;
    installation_name?: string | null;
    organization_uuid: string | null;
    /** Populated when API resolves org name; optional until backend supports it. */
    organization_name?: string | null;
    athlete_uuid?: string | null;
    athlete_name?: string | null;
    shipment_uuid: string | null;
    unit_cost_cents: number | null;
    notes: string | null;
    received_at: string | null;
    deployed_at: string | null;
    decommissioned_at: string | null;
    created_at: string;
    updated_at: string;
    /**
     * When a user last interacted with this pod: the newest chain-of-custody or firmware
     * event (falls back to the pod row's updated_at). Drives the "Updated" column.
     */
    last_activity_at?: string | null;
    custody_events?: Fp1CustodyEvent[];
    /** Hierarchy pod UUID when a `pods` row matches this unit by serial (for admin pod APIs). */
    pod_uuid?: string | null;
    /** Hierarchy custody (pods.custody_status); distinct from FP1 warehouse `status`. */
    custody_status?: string | null;
    assigned_org?: Fp1DeviceAssignedOrgRef | null;
    assigned_athlete?: Fp1DeviceAssignedAthleteRef | null;
    pending_transfer_to?: Fp1DevicePendingTransferRef | null;
    last_shipped_at?: string | null;
    last_returned_at?: string | null;
    hierarchy_pod_decommissioned_at?: string | null;
    stripe_linked_subscription?: Fp1DeviceStripeLinkedSubscription | null;
    /** Total deployed non-comp pods for the org owning this device (null for athlete / comp / warehouse pods). */
    org_pod_count?: number | null;
    /** Total subscription seat quantity (sum of subscription_items.quantity) for the org's active pod subscription. */
    org_subscription_quantity?: number | null;
    /** Number of (non-deleted) uploads for this pod in the last 30 days. Null when the pod has no upload history. */
    usage_count_30d?: number | null;
    /** Most recent upload activity_date for this pod (YYYY-MM-DD). Null when never used. */
    last_used_at?: string | null;
    /** Subscription start date (YYYY-MM-DD) for deployed pods; drives "held for" on the Management subtab. */
    subscription_started_at?: string | null;
    /** Whole months behind for past-due deployed pods; null otherwise. Splits Past Due vs In Collection. */
    months_past_due?: number | null;
    /**
     * Revenue holder for a deployed pod: 'org' (org pays) or 'athlete' (Transferred,
     * athlete pays their own subscription). Null for non-deployed/comp. Used by the
     * Management summary to split Subscribed / Past due / In collection by holder.
     */
    subscription_holder?: 'org' | 'athlete' | null;
    // ── Live Stripe discount / credit ───────────────────────────────────────────
    // Only populated when the device feed is requested with include_billing (the
    // Management subtab) AND the pod sits on an *active* Stripe subscription that the
    // billing pull resolved. Null everywhere else (past-due, no-sub, warehouse, comp,
    // or when billing wasn't requested). All money fields are USD, keyed per the pod's
    // subscription — for an org sub covering many pods the value is the whole sub's pod
    // portion, so de-duplicate by subscription/holder before summing.
    /** Real recurring monthly pod charge AFTER coupons (USD). */
    stripe_net_monthly?: number | null;
    /** Monthly pod charge BEFORE any discount (USD); net < gross means a discount applies. */
    stripe_gross_monthly?: number | null;
    /** True when a 100%-off coupon or standing credit currently makes the pod bill $0. */
    stripe_zero_charge?: boolean | null;
    /** True when a coupon (partial or full) actually reduces the pod charge. */
    stripe_has_discount?: boolean | null;
    /** Human label for the coupon, e.g. "50% off" / "$20.00 off"; null when none. */
    stripe_discount_label?: string | null;
    /** Standing customer credit available, in cents (>= 0). */
    stripe_credit_cents?: number | null;
    /**
     * The Stripe subscription id the discount/credit lives on (resolved server-side —
     * the pod's own sub, or the org's active fiyrpod sub). Lets the Management subtab
     * deep-link to dashboard.stripe.com. Null when no billing was resolved.
     */
    stripe_billing_subscription_id?: string | null;
}

/**
 * Statuses where buyer org / athlete are not kept on the device (matches backend FP1 rules).
 * Treat org/athlete as absent in UI even if legacy DB rows still store UUIDs.
 */
export const FP1_STATUSES_WITHOUT_BUYER_ASSIGNMENT: Fp1DeviceStatus[] = [
    'in_stock',
    'defective',
    'unknown',
    'maintenance',
    'returned',
    'decommissioned',
];

export function fp1StatusRetainsBuyerAssignment(status: Fp1DeviceStatus): boolean {
    return !FP1_STATUSES_WITHOUT_BUYER_ASSIGNMENT.includes(status);
}

export function fp1EffectiveOrganizationUuid(device: Fp1Device): string | null {
    if (!fp1StatusRetainsBuyerAssignment(device.status)) return null;
    const u = device.organization_uuid?.trim();
    return u || null;
}

export function fp1EffectiveAthleteUuid(device: Fp1Device): string | null {
    if (!fp1StatusRetainsBuyerAssignment(device.status)) return null;
    const u = device.athlete_uuid?.trim();
    return u || null;
}

export function fp1EffectiveOrganizationName(device: Fp1Device): string | null {
    if (!fp1StatusRetainsBuyerAssignment(device.status)) return null;
    const n = device.organization_name?.trim();
    return n || null;
}

export function fp1EffectiveAthleteName(device: Fp1Device): string | null {
    if (!fp1StatusRetainsBuyerAssignment(device.status)) return null;
    const n = device.athlete_name?.trim();
    return n || null;
}

export function fp1StripeLinkedStatus(device: Fp1Device): string | null {
    const s = device.stripe_linked_subscription?.subscription_status?.trim().toLowerCase();
    return s || null;
}

export function fp1StripeSubscriptionNeedsAttention(device: Fp1Device): boolean {
    const s = fp1StripeLinkedStatus(device);
    return s === 'past_due' || s === 'unpaid';
}

export interface Fp1Shipment {
    uuid: string;
    supplier_name: string;
    po_number: string | null;
    received_by_uuid: string | null;
    received_by_name: string | null;
    received_at: string;
    total_cost_cents: number | null;
    notes: string | null;
    created_at: string;
    updated_at: string;
    devices?: Fp1Device[];
}

export interface Fp1Installation {
    uuid: string;
    name: string;
    organization_uuid: string | null;
    location_address: string | null;
    location_notes: string | null;
    status: Fp1InstallationStatus;
    gateway_uuid: string | null;
    iot_thing_name: string | null;
    device_name: string | null;
    iot_certificate_id?: string | null;
    installed_at: string | null;
    created_at: string;
    updated_at: string;
    devices?: Fp1Device[];
    fp1_unit_count?: number;
    fp1_unit_devices?: Fp1Device[];
}

export interface Fp1BenchImportPayload {
    /** Column 1: Date/Time tested */
    tested_at?: string | null;
    /** Column 2: Current without FW */
    current_draw?: string | null;
    /** Column 4 on 12-col export; legacy short exports may place FW in the last column */
    fw_version?: string | null;
    /** Column 5: Standby current (12-col); column 4 on prior 11-col layout */
    current_2?: string | null;
    /** Column 6 / 5 */
    current_3?: string | null;
    /** Column 7 / 6 */
    current_4?: string | null;
    vbat?: string | null;
    pmid?: string | null;
    iin?: string | null;
    ichg?: string | null;
    /** Column 12 on 12-col export; if non-empty, device is stored as defective */
    error_message?: string | null;
}

export interface CreateFp1ShipmentDevice {
    serial_number: string;
    device_type: Fp1DeviceType;
    /** Legacy PCB label per row (matches API `devices.*.model`). */
    model: string;
    /** Optional per-device override; otherwise use shipment `default_hardware_version`. */
    hardware_version?: string | null;
    /** Usually from bench CSV column 4 (`fw_version`); editable per row before submit. */
    firmware_version?: string | null;
    unit_cost_cents?: number;
    notes?: string;
    /** Stored as a custody event with structured metadata (factory CSV / spreadsheet import). */
    bench_import?: Fp1BenchImportPayload;
}

export interface Fp1BatteryHealthDevice {
    uuid: string;
    serial_number: string;
    status: Fp1DeviceStatus;
    firmware_version: string | null;
    organization_uuid: string | null;
    organization_name: string | null;
    athlete_uuid: string | null;
    athlete_name: string | null;
    athlete_email: string | null;
    athlete_phone: string | null;
    drain_per_hour: number | null;
    battery_health: 'good' | 'fair' | 'poor' | 'no_data';
    upload_count: number;
    last_activity_date: string | null;
    last_deployed_at: string | null;
    /** How many recent sessions (≤5) the battery_health rating was averaged over. */
    sessions_averaged: number;
    /** % battery used during the most recent session (from the stored battery_drain). */
    last_session_battery_pct: number | null;
    last_session_duration_min: number | null;
    last_session_date: string | null;
}

export interface CreateFp1ShipmentParams {
    supplier_name: string;
    po_number?: string;
    received_at: string;
    notes?: string;
    /** PCB / hardware revision for every device in this shipment (locked after create). */
    default_hardware_version?: string | null;
    devices: CreateFp1ShipmentDevice[];
}

// ── Debugging tab ──────────────────────────────────────────────────────────────

export interface Fp1DebugUpload {
    uuid: string;
    individual_uuid: string | null;
    user_uuid: string | null;
    athlete_name: string | null;
    pod_serial_number: string | null;
    pod_fw_version: string | null;
    activity_date: string | null;
    start_date_time: string | null;
    sprint_data_type: string | null;
    // Numeric summary fields arrive as strings (raw query builder, MySQL decimals).
    summary_max_velocity: number | string | null;
    summary_max_acceleration: number | string | null;
    summary_max_deceleration: number | string | null;
    summary_number_of_accelerations: number | string | null;
    summary_number_of_decelerations: number | string | null;
    error: string | null;
    completed_at: string | null;
    has_download: boolean;
}

export interface Fp1DebugSessionAthlete {
    individual_uuid: string;
    name: string;
}

export interface Fp1DebugSession {
    // org + upload_grouping_uuid (or start_date_time) — unique per session, even on the same day
    session_key: string;
    upload_grouping_uuid: string | null;
    title: string | null;
    start_date_time: string | null;
    start_timezone: string | null;
    date: string | null;
    organization_uuid: string | null;
    organization_name: string | null;
    athlete_count: number;
    pod_count: number;
    upload_count: number;
    athletes: Fp1DebugSessionAthlete[];
    uploads: Fp1DebugUpload[];
}

export type Fp1DebugSessionExportStatus = 'queued' | 'running' | 'ready' | 'failed' | 'unknown';

export interface Fp1DebugSessionExport {
    export_id: string;
    status: Fp1DebugSessionExportStatus;
    stage: string | null;
    progress: number | null;
    error: string | null;
    file_name: string | null;
    bytes: number | null;
    download_url: string | null;
}

export interface Fp1DebugDownloadResult {
    upload: {
        uuid: string;
        activity_date: string | null;
        pod_serial_number: string | null;
        pod_fw_version: string | null;
        file_name: string | null;
        sprint_data_type: string | null;
        summary_max_velocity: number | null;
        summary_max_acceleration: number | null;
        summary_max_deceleration: number | null;
        summary_number_of_accelerations: number | null;
        summary_number_of_decelerations: number | null;
        error: string | null;
        completed_at: string | null;
    };
    download_url: string;
    expires_at: string;
}

export interface UpdateFp1DeviceParams {
    firmware_version?: string | null;
    status?: Fp1DeviceStatus;
    installation_uuid?: string | null;
    organization_uuid?: string | null;
    athlete_uuid?: string | null;
    unit_cost_cents?: number;
    notes?: string;
    deployed_at?: string;
    decommissioned_at?: string;
    event_notes?: string;
}

/**
 * Manual subscription-status override for a pod. Sets the pod to exactly one bucket —
 * Comp (free) or Invoiced (externally billed via Stripe) — and records a custody event.
 */
export type Fp1SubscriptionOverrideParams =
    | {
          type: 'comp';
          /** Who is comping the device (typed by the admin). */
          comped_by: string;
          /** Single free-text box: why it's comped and for how long. */
          comp_reason: string;
      }
    | {
          type: 'invoiced';
          /** Stripe invoice number. */
          external_invoice_id: string;
          /** Date the invoice covers the pod through (YYYY-MM-DD). */
          external_invoice_end_date: string;
      }
    | {
          /**
           * Remove every subscription status from the pod: comp, external invoice and the
           * pod's own Stripe subscription link are all cleared, and the pod is stamped as
           * deliberately unsubscribed so it reads "No Sub" instead of inheriting its
           * organization's subscription status.
           */
          type: 'none';
          /** Optional free-text explanation, appended to the custody event note. */
          reason?: string;
      };
